// src/pages/container_rental/ContainerRentalDetailPage.tsx

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

import {
  ContainerRentalDto,
  RentalScheduleDto,
} from "../../contracts/container_rental/types.ts";
import { useContainerRentalApi } from "../../hooks/useContainerRentalApi.ts";
import StatusChip from "./components/StatusChip.tsx";
import RentalScheduleTable from "./components/RentalScheduleTable.tsx";
import IssueRoundDialog from "./components/IssueRoundDialog.tsx";
import SimpleReasonDialog from "./components/SimpleReasonDialog.tsx";
import ExtendRentalDialog from "./components/ExtendRentalDialog.tsx";
import ConfirmDialog from "../estimates/ConfirmDialog.tsx";

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("YYYY.MM.DD") : iso;
};

const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("YYYY.MM.DD HH:mm") : iso;
};

const fmtKrw = (n: number) =>
  Number.isFinite(n) ? new Intl.NumberFormat("ko-KR").format(n) + "원" : "—";

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, gap: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

export default function ContainerRentalDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id = "" } = useParams<{ id: string }>();
  const basePath = location.pathname.startsWith("/dashboard")
    ? "/dashboard/container-rental"
    : "/container-rental";

  const {
    getRental,
    deleteRental,
    issueRound,
    skipRound,
    cancelRound,
    closeRental,
    reopenRental,
    extendRental,
    downloadRentalExcel,
  } = useContainerRentalApi();

  const [rental, setRental] = useState<ContainerRentalDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: "error" | "success" } | null>(null);

  const [issueDialog, setIssueDialog] = useState<RentalScheduleDto | null>(null);
  const [skipDialog, setSkipDialog] = useState<RentalScheduleDto | null>(null);
  const [cancelDialog, setCancelDialog] = useState<RentalScheduleDto | null>(null);
  const [extendOpen, setExtendOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRental = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getRental(id);
      setRental(r);
    } catch (e: any) {
      setError(e?.message ?? "거래를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id) fetchRental();
  }, [id, fetchRental]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(basePath)} sx={{ mb: 2 }}>
          목록으로
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!rental) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(basePath)} sx={{ mb: 2 }}>
          목록으로
        </Button>
        <Alert severity="warning">거래 정보를 찾을 수 없습니다.</Alert>
      </Box>
    );
  }

  const schedules = rental.schedules ?? [];

  // Action handlers ---------------------------------------------------

  const handleIssueSubmit = async (values: { invoiceNumber: string; issuedAt: string; issuedMemo?: string }) => {
    if (!issueDialog) return;
    setActionLoading(true);
    try {
      await issueRound(rental.id, issueDialog.round, values);
      setIssueDialog(null);
      setSnack({ msg: `${issueDialog.round}회차 발행 완료 처리되었습니다.`, severity: "success" });
      await fetchRental();
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "발행 처리 실패", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkipSubmit = async (values: { reason?: string }) => {
    if (!skipDialog) return;
    setActionLoading(true);
    try {
      await skipRound(rental.id, skipDialog.round, values);
      setSkipDialog(null);
      setSnack({ msg: `${skipDialog.round}회차를 건너뜀 처리했습니다.`, severity: "success" });
      await fetchRental();
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "건너뜀 처리 실패", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRoundSubmit = async (values: { reason?: string }) => {
    if (!cancelDialog) return;
    setActionLoading(true);
    try {
      await cancelRound(rental.id, cancelDialog.round, values);
      setCancelDialog(null);
      setSnack({ msg: `${cancelDialog.round}회차를 취소했습니다.`, severity: "success" });
      await fetchRental();
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "회차 취소 실패", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendSubmit = async (values: { addRounds: number }) => {
    setActionLoading(true);
    try {
      await extendRental(rental.id, values);
      setExtendOpen(false);
      setSnack({ msg: `${values.addRounds}회차 연장되었습니다.`, severity: "success" });
      await fetchRental();
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "연장 실패", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async () => {
    setActionLoading(true);
    try {
      await closeRental(rental.id);
      setCloseConfirmOpen(false);
      setSnack({ msg: "거래를 종료했습니다.", severity: "success" });
      await fetchRental();
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "종료 실패", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async () => {
    setActionLoading(true);
    try {
      await reopenRental(rental.id);
      setReopenConfirmOpen(false);
      setSnack({ msg: "거래를 재개했습니다.", severity: "success" });
      await fetchRental();
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "재개 실패", severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await deleteRental(rental.id);
      setSnack({ msg: "거래를 삭제했습니다.", severity: "success" });
      navigate(basePath);
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "삭제 실패", severity: "error" });
      setActionLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const { blob, filename } = await downloadRentalExcel(rental.id);
      saveAs(blob, filename);
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "엑셀 다운로드 실패", severity: "error" });
    }
  };

  const isClosed = rental.status === "closed";
  const isCancelled = rental.status === "cancelled";

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(basePath)} sx={{ mb: 2 }}>
        목록으로
      </Button>

      {/* Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {rental.clientName || "(거래처 미입력)"}
                </Typography>
                <StatusChip status={rental.status} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {rental.siteName || "(현장 미입력)"}
                {rental.siteAddress ? ` · ${rental.siteAddress}` : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                작성: {fmtDateTime(rental.createdTimestamp)} · 수정: {fmtDateTime(rental.modifiedTimestamp)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`${basePath}/edit/${rental.id}`)}
              >
                수정
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadExcel}
              >
                엑셀
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setExtendOpen(true)}
                disabled={isClosed || isCancelled}
              >
                회차 연장
              </Button>
              {isClosed ? (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RestartAltIcon />}
                  onClick={() => setReopenConfirmOpen(true)}
                >
                  재개
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<StopCircleIcon />}
                  onClick={() => setCloseConfirmOpen(true)}
                  disabled={isCancelled}
                >
                  거래 종료
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                삭제
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Summary panels */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                컨테이너 정보
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <InfoRow label="모델" value={rental.containerModel || "—"} />
              <InfoRow label="수량" value={`${rental.containerCount}대`} />
              <InfoRow label="월 공급가 합계" value={fmtKrw(rental.monthlyRent)} />
              <InfoRow label="왕복 운반비" value={fmtKrw(rental.transportFee)} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                발행 정보
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <InfoRow label="초기 선결제" value={`${rental.initialMonths}개월`} />
              <InfoRow label="총 회차" value={`${rental.totalRounds}회차`} />
              <InfoRow label="담당자" value={rental.contactPerson || "—"} />
              <InfoRow label="연락처" value={rental.contactPhone || "—"} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                핵심 일자
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <InfoRow label="발행일" value={fmtDate(rental.issueDate)} />
              <InfoRow label="1회차 시작" value={fmtDate(rental.rentalStartDate)} />
              {rental.closedAt && <InfoRow label="종료일" value={fmtDateTime(rental.closedAt)} />}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {rental.memo && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              메모
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {rental.memo}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Schedule table */}
      <Box sx={{ mb: 1, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          발행 일정
        </Typography>
        <Typography variant="caption" color="text.secondary">
          총 {schedules.length}회차
        </Typography>
      </Box>
      <RentalScheduleTable
        schedules={schedules}
        onIssue={(r) => setIssueDialog(r)}
        onSkip={(r) => setSkipDialog(r)}
        onCancel={(r) => setCancelDialog(r)}
      />

      {/* TODO: Activity log section. Backend records audit data but no GET
          endpoint is exposed yet (see phase 2 backend report). Hide until
          the endpoint is wired. */}

      {/* Dialogs */}
      <IssueRoundDialog
        open={Boolean(issueDialog)}
        round={issueDialog?.round ?? 0}
        onClose={() => setIssueDialog(null)}
        onSubmit={handleIssueSubmit}
        loading={actionLoading}
      />

      <SimpleReasonDialog
        open={Boolean(skipDialog)}
        title={skipDialog ? `${skipDialog.round}회차 건너뜀` : "회차 건너뜀"}
        description="해당 회차를 건너뜀 처리합니다. 사유를 남겨두면 나중에 추적하기 쉽습니다."
        confirmText="건너뜀 처리"
        confirmColor="warning"
        onClose={() => setSkipDialog(null)}
        onSubmit={handleSkipSubmit}
        loading={actionLoading}
      />

      <SimpleReasonDialog
        open={Boolean(cancelDialog)}
        title={cancelDialog ? `${cancelDialog.round}회차 취소` : "회차 취소"}
        description="해당 회차를 취소 처리합니다."
        confirmText="취소 처리"
        confirmColor="error"
        onClose={() => setCancelDialog(null)}
        onSubmit={handleCancelRoundSubmit}
        loading={actionLoading}
      />

      <ExtendRentalDialog
        open={extendOpen}
        currentTotal={rental.totalRounds}
        onClose={() => setExtendOpen(false)}
        onSubmit={handleExtendSubmit}
        loading={actionLoading}
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        title="거래를 종료하시겠습니까?"
        message="종료하면 새 회차 발행이 중단됩니다. 필요하면 나중에 다시 재개할 수 있습니다."
        onClose={() => setCloseConfirmOpen(false)}
        onConfirm={handleClose}
        loading={actionLoading}
        confirmText="종료"
        cancelText="아니오"
      />

      <ConfirmDialog
        open={reopenConfirmOpen}
        title="거래를 재개하시겠습니까?"
        message="재개 시 거래 상태가 진행중으로 변경됩니다."
        onClose={() => setReopenConfirmOpen(false)}
        onConfirm={handleReopen}
        loading={actionLoading}
        confirmText="재개"
        cancelText="아니오"
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="거래를 삭제하시겠습니까?"
        message="이 작업은 되돌릴 수 없습니다."
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        loading={actionLoading}
        confirmText="삭제"
        cancelText="아니오"
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
